import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  BookOpen, Save, Plus, Edit, GripVertical, Eye, EyeOff, X,
  GraduationCap, School, Users, Calculator, Microscope, Palette,
  Music, Globe, Trophy, Star, Heart, Zap, Target, Shield,
  Layers, Trash2
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useParametres } from '../contexts/ParametresContext';
import { Toast } from './ui/Toast';
import ChargementSection from './parametrage/ChargementSection';
import ConfirmDialog from './ConfirmDialog';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

const ParametresClasseMatiere = ({ onBackToDashboard }) => {
  const { 
    parametres, 
    loading, 
    updateSection, 
    createSection,
    deleteSection,
    reorderSections
  } = useParametres();

  // États optimistes pour les sections
  const [sectionsOptimistes, setSectionsOptimistes] = useState(parametres.sections);
  const [sectionsEnCours, setSectionsEnCours] = useState(new Set());
  const modificationsEnCours = useRef(new Map());

  // États locaux
  const [editingSection, setEditingSection] = useState(null);
  const [showNewSectionModal, setShowNewSectionModal] = useState(false);
  // La section qu'on s'apprete a supprimer. Un objet, pas un booleen : le
  // dialogue a besoin de son nom pour que la confirmation dise quoi.
  const [sectionASupprimer, setSectionASupprimer] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [currentToast, setCurrentToast] = useState(null);

  // Formulaire nouvelle section
  const [formSection, setFormSection] = useState({
    nom: '',
    couleur: '#246990',
    icone: 'GraduationCap',
    description: ''
  });

  // Synchronisation avec le context
  useEffect(() => {
    setSectionsOptimistes(prevSections => {
      const nouvelles = parametres.sections.map(sectionContext => {
        const modifEnCours = modificationsEnCours.current.get(sectionContext.id);
        if (modifEnCours) {
          return modifEnCours;
        }
        return sectionContext;
      });
      return nouvelles;
    });
  }, [parametres.sections]);

  // Toast helper
  const addToast = useCallback((message, type = 'success') => {
    setCurrentToast({ message, type });
  }, []);


  // Toggle section avec état optimiste
  const handleToggleSection = useCallback(async (sectionId) => {
    const sectionActuelle = sectionsOptimistes.find(s => s.id === sectionId);
    if (!sectionActuelle || sectionsEnCours.has(sectionId)) return;

    const nouvelEtat = !sectionActuelle.active;
    setSectionsEnCours(prev => new Set([...prev, sectionId]));
    
    const sectionOptimiste = { ...sectionActuelle, active: nouvelEtat };
    modificationsEnCours.current.set(sectionId, sectionOptimiste);
    
    setSectionsOptimistes(prev => 
      prev.map(s => s.id === sectionId ? sectionOptimiste : s)
    );

    try {
      const result = await updateSection(sectionId, sectionOptimiste);
      if (result.success) {
        addToast(`Section ${nouvelEtat ? 'activée' : 'désactivée'}`);
      } else {
        setSectionsOptimistes(prev => 
          prev.map(s => s.id === sectionId ? sectionActuelle : s)
        );
        addToast(result.message || 'Erreur modification', 'error');
      }
    } catch (error) {
      setSectionsOptimistes(prev => 
        prev.map(s => s.id === sectionId ? sectionActuelle : s)
      );
      addToast('Erreur lors de la modification', 'error');
    } finally {
      setSectionsEnCours(prev => {
        const nouvelle = new Set(prev);
        nouvelle.delete(sectionId);
        return nouvelle;
      });
      modificationsEnCours.current.delete(sectionId);
    }
  }, [sectionsOptimistes, sectionsEnCours, updateSection, addToast]);

  // Sauvegarde section
  const handleSaveSection = useCallback(async (sectionId, donneesSection) => {
    try {
      const result = await updateSection(sectionId, donneesSection);
      if (result.success) {
        addToast('Section mise à jour');
        setEditingSection(null);
        setSectionsOptimistes(prev => 
          prev.map(s => s.id === sectionId ? { ...s, ...donneesSection } : s)
        );
      } else {
        addToast(result.message || 'Erreur mise à jour', 'error');
      }
    } catch (error) {
      addToast('Erreur lors de la sauvegarde', 'error');
    }
  }, [updateSection, addToast]);

  // Création nouvelle section
  const handleCreateSection = useCallback(async () => {
    if (!formSection.nom.trim()) {
      addToast('Le nom de la section est requis', 'error');
      return;
    }

    const result = await createSection(formSection);
    if (result.success) {
      addToast('Section créée avec succès');
      setShowNewSectionModal(false);
      setFormSection({ nom: '', couleur: '#246990', icone: 'GraduationCap', description: '' });
    } else {
      addToast(result.message || 'Erreur création', 'error');
    }
  }, [formSection, createSection, addToast]);

  /**
   * Supprimer une section.
   *
   * Le serveur refuse avec un 409 si elle contient des classes, et dit
   * combien. On ne supprime jamais en cascade : effacer une section
   * emporterait ses classes, leurs eleves et leurs notes. Une section qui ne
   * sert plus se desactive — c'est l'oeil barre juste a cote.
   */
  const handleDeleteSection = useCallback(async () => {
    if (!sectionASupprimer) return;

    setSuppressionEnCours(true);
    try {
      const result = await deleteSection(sectionASupprimer.id);
      if (result.success) {
        addToast(result.message || 'Section supprimée');
        setSectionASupprimer(null);
      } else {
        // Le message du serveur nomme le nombre de classes : on le garde tel
        // quel plutot que de le remplacer par « Erreur de suppression ».
        addToast(result.message || 'Suppression impossible', 'error');
      }
    } finally {
      setSuppressionEnCours(false);
    }
  }, [sectionASupprimer, deleteSection, addToast]);

  // Drag & Drop
  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination) return;

    const sections = Array.from(sectionsOptimistes);
    const [moved] = sections.splice(result.source.index, 1);
    sections.splice(result.destination.index, 0, moved);

    const nouveauxOrdres = sections.map((section, index) => ({
      id: section.id,
      ordre: index + 1
    }));

    const result_api = await reorderSections(nouveauxOrdres);
    if (result_api.success) {
      addToast('Sections réorganisées');
    } else {
      addToast('Erreur réorganisation', 'error');
    }
  }, [sectionsOptimistes, reorderSections, addToast]);

  // Configuration UI - Couleurs et icônes modernes
  const couleursPredefinies = [
    '#246990', '#319190', '#c66d59', '#8b5a3c',
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'
  ];

  // Icônes Lucide React modernes
  const iconesLucide = [
    { name: 'GraduationCap', component: GraduationCap },
    { name: 'BookOpen', component: BookOpen },
    { name: 'School', component: School },
    { name: 'Users', component: Users },
    { name: 'Calculator', component: Calculator },
    { name: 'Microscope', component: Microscope },
    { name: 'Palette', component: Palette },
    { name: 'Music', component: Music },
    { name: 'Globe', component: Globe },
    { name: 'Trophy', component: Trophy },
    { name: 'Star', component: Star },
    { name: 'Heart', component: Heart },
    { name: 'Zap', component: Zap },
    { name: 'Target', component: Target },
    { name: 'Shield', component: Shield }
  ];

  // Helper pour obtenir le composant d'icône
  const getIconComponent = (iconName) => {
    const iconObj = iconesLucide.find(icon => icon.name === iconName);
    return iconObj ? iconObj.component : GraduationCap;
  };


  if (loading) {
    // L'ecran de chargement n'avait ni en-tete ni libelle : on ne pouvait ni
    // revenir en arriere, ni savoir ce qu'on attendait.
    return (
      <div className="w-full">
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          titre="Paramétrage des classes et matières"
          sousTitre="Sections, niveaux, classes et matières enseignées"
        />
        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <ChargementSection accent="#246990" libelle="Chargement des sections…" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={Layers}
        titre="Paramétrage des classes et matières"
        sousTitre="Sections, niveaux, classes et matières enseignées"
      />

      {/* Toast */}
      {currentToast && (
        <Toast 
          message={currentToast.message}
          type={currentToast.type}
          onClose={() => setCurrentToast(null)}
        />
      )}

    

      {/* Contenu principal */}
      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
        <EnTeteSection
          icone={BookOpen}
          titre="Sections du système"
          sousTitre="Les niveaux de votre établissement, dans leur ordre d'affichage"
          accent="#246990"
          accentSombre="#1a4f6e"
          actions={
            <button
              onClick={() => setShowNewSectionModal(true)}
              className="flex h-9 items-center justify-center gap-2 rounded-lg bg-[#246990] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#1f5a7a]"
            >
              <Plus className="w-4 h-4" />
              Ajouter une section
            </button>
          }
        />

        {/* Les trois compteurs tenaient dans trois encadrés dégradés de trois
            couleurs différentes. Ils disent la même chose dans le compteur de
            la carte, là où la norme place les décomptes. */}
        <CarteReglages
          titre="Sections"
          accent="#246990"
          compteur={`${sectionsOptimistes.filter((x) => x.active).length} active${
            sectionsOptimistes.filter((x) => x.active).length > 1 ? 's' : ''
          } sur ${sectionsOptimistes.length}`}
        >
        {/* Liste des sections avec drag & drop */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                {sectionsOptimistes.map((section, index) => (
                  <ModernSectionCard
                    key={section.id}
                    section={section}
                    index={index}
                    isLoading={sectionsEnCours.has(section.id)}
                    onEdit={setEditingSection}
                    onSave={handleSaveSection}
                    onToggle={handleToggleSection}
                    onDelete={setSectionASupprimer}
                    isEditing={editingSection === section.id}
                    couleursPredefinies={couleursPredefinies}
                    iconesLucide={iconesLucide}
                    getIconComponent={getIconComponent}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        </CarteReglages>
        </div>
      </div>

      {/* Le modal etait un calque monte a la main — `fixed inset-0` avec son
          propre voile — alors que CLAUDE.md impose les primitives `Dialog` :
          Radix pose `pointer-events: none` sur le body tant qu'un Dialog est
          ouvert, et un overlay maison rendu hors de son portail s'affiche
          normalement mais ne recoit aucun clic. */}
      <Dialog open={showNewSectionModal} onOpenChange={setShowNewSectionModal}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Plus size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Nouvelle section
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Un niveau d'enseignement : primaire, collège, lycée, technique…
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-700">Nom de la section *</Label>
                  <Input
                    value={formSection.nom}
                    onChange={(e) => setFormSection(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Technique, Formation professionnelle…"
                    className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  />
                </div>

                <div>
                  <Label className="text-sm text-gray-700">Description</Label>
                  <Textarea
                    value={formSection.description}
                    onChange={(e) => setFormSection(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Ce que cette section regroupe"
                    className="mt-1 resize-none rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-700">Couleur</Label>
                  <div className="mt-1 grid grid-cols-8 gap-2">
                    {couleursPredefinies.map(couleur => (
                      <button
                        key={couleur}
                        type="button"
                        onClick={() => setFormSection(prev => ({ ...prev, couleur }))}
                        aria-label={`Couleur ${couleur}`}
                        aria-pressed={formSection.couleur === couleur}
                        className={`h-8 w-8 rounded-lg border-2 transition-all ${
                          formSection.couleur === couleur
                            ? 'border-gray-800 ring-2 ring-gray-800 ring-offset-2'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: couleur }}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-gray-700">Icône</Label>
                  <div className="mt-1 grid grid-cols-8 gap-2">
                    {iconesLucide.map(({ name, component: IconComponent }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setFormSection(prev => ({ ...prev, icone: name }))}
                        aria-label={name}
                        aria-pressed={formSection.icone === name}
                        className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 transition-all ${
                          formSection.icone === name
                            ? 'border-[#246990] bg-[#eef7fb] text-[#246990]'
                            : 'border-gray-200 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        <IconComponent className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Apercu */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <Eye className="h-3.5 w-3.5" />
                Aperçu
              </p>
              <div className="flex items-center gap-4">
                <div
                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-white"
                  style={{ backgroundColor: formSection.couleur }}
                >
                  {React.createElement(getIconComponent(formSection.icone), { className: 'w-6 h-6' })}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {formSection.nom || 'Nom de la section'}
                  </p>
                  <p className="mt-0.5 truncate text-[11px] text-gray-500">
                    {formSection.description || 'Aucune description'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowNewSectionModal(false)}
              className="h-9 min-w-[168px] justify-center gap-2"
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>
            <Button
              onClick={handleCreateSection}
              disabled={!formSection.nom.trim()}
              className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300"
            >
              <Plus className="h-4 w-4" />
              Créer la section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CLAUDE.md demande `ConfirmDialog` pour une action irreversible. */}
      <ConfirmDialog
        isOpen={Boolean(sectionASupprimer)}
        onClose={() => setSectionASupprimer(null)}
        onConfirm={handleDeleteSection}
        isLoading={suppressionEnCours}
        type="error"
        title={`Supprimer « ${sectionASupprimer?.nom || ''} » ?`}
        description={
          "Cette section disparaîtra définitivement. La suppression est refusée si "
          + "elle contient encore des classes — dans ce cas, désactivez-la plutôt : "
          + "elle reste en place et cesse d'apparaître."
        }
        confirmText="Supprimer"
      />
    </div>
  );
};




// Composant carte section modernisé avec icônes Lucide
const ModernSectionCard = ({ 
  section, 
  index, 
  isLoading,
  onEdit,
  onSave,
  onToggle,
  onDelete,
  isEditing, 
  couleursPredefinies, 
  iconesLucide,
  getIconComponent
}) => {
  const [editForm, setEditForm] = useState({
    nom: section.nom,
    couleur: section.couleur,
    icone: section.icone,
    active: section.active,
    description: section.description || ''
  });

  useEffect(() => {
    if (!isEditing) {
      setEditForm({
        nom: section.nom,
        couleur: section.couleur,
        icone: section.icone,
        active: section.active,
        description: section.description || ''
      });
    }
  }, [section, isEditing]);

  const handleSave = useCallback(() => {
    onSave(section.id, editForm);
  }, [section.id, editForm, onSave]);

  const handleCancel = useCallback(() => {
    setEditForm({
      nom: section.nom,
      couleur: section.couleur,
      icone: section.icone,
      active: section.active,
      description: section.description || ''
    });
    onEdit(null);
  }, [section, onEdit]);

  const handleToggleClick = useCallback(() => {
    onToggle(section.id);
  }, [section.id, onToggle]);

  return (
    <Draggable draggableId={section.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`bg-white border rounded-xl p-6 transition-all duration-200 ${
            snapshot.isDragging 
              ? 'shadow-2xl scale-105 rotate-2' 
              : 'shadow-sm hover:shadow-md'
          } ${isEditing ? 'ring-2 ring-[#246990] bg-blue-50/30' : ''} ${
            isLoading ? 'opacity-75' : ''
          }`}
        >
          <div className="flex items-start gap-4">
            {/* Handle de drag */}
            <div {...provided.dragHandleProps} className="cursor-grab hover:cursor-grabbing mt-1">
              <GripVertical className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors" />
            </div>

            {/* Icône de la section avec effet moderne */}
            <div className="relative">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-105"
                style={{ 
                  backgroundColor: isEditing ? editForm.couleur : section.couleur,
                  boxShadow: `0 8px 25px ${(isEditing ? editForm.couleur : section.couleur)}33`
                }}
              >
                {React.createElement(
                  getIconComponent(isEditing ? editForm.icone : section.icone), 
                  { className: "w-7 h-7 text-white" }
                )}
              </div>
              {isLoading && (
                <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* Contenu principal */}
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="space-y-4">
                  {/* Nom en édition */}
                  <input
                    type="text"
                    value={editForm.nom}
                    onChange={(e) => setEditForm(prev => ({ ...prev, nom: e.target.value }))}
                    className="w-full px-4 py-3 border rounded-lg focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none transition-all"
                    placeholder="Nom de la section"
                  />
                  
                  {/* Couleurs et icônes */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-2">Couleur</label>
                      <div className="flex flex-wrap gap-2">
                        {couleursPredefinies.slice(0, 6).map(couleur => (
                          <button
                            key={couleur}
                            onClick={() => setEditForm(prev => ({ ...prev, couleur }))}
                            className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                              editForm.couleur === couleur ? 'border-gray-800 shadow-lg' : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: couleur }}
                            title={couleur}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-2">Icône</label>
                      <div className="flex flex-wrap gap-2">
                        {iconesLucide.slice(0, 6).map(({ name, component: IconComponent }) => (
                          <button
                            key={name}
                            onClick={() => setEditForm(prev => ({ ...prev, icone: name }))}
                            className={`w-8 h-8 border-2 rounded-lg transition-all hover:scale-110 flex items-center justify-center ${
                              editForm.icone === name ? 'border-[#246990] bg-blue-50 text-[#246990]' : 'border-gray-300 text-gray-600'
                            }`}
                            title={name}
                          >
                            <IconComponent className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Toggle actif/inactif */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.active}
                      onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
                      className="w-4 h-4 text-[#246990] rounded"
                    />
                    <span className="text-sm text-gray-700 font-medium">Section active</span>
                  </label>

                  {/* Description */}
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 border rounded-lg text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20 outline-none transition-all resize-none"
                    rows="2"
                    placeholder="Description de la section..."
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <h4 className="min-w-0 flex-1 truncate font-semibold text-gray-900 text-lg">
                      {section.nom}
                    </h4>
                    {isLoading && (
                      <span className="inline-flex h-6 w-[96px] flex-shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-100 text-xs font-medium text-blue-700">
                        En cours…
                      </span>
                    )}
                    {/* Largeur fixe et pousse a droite : le badge suivait le
                        titre, donc sa position dependait de la longueur du nom
                        et rien ne s'alignait d'une ligne a l'autre.
                        DESIGN-SYSTEM.md, section Badges. */}
                    <span className={`inline-flex h-6 w-[84px] flex-shrink-0 items-center justify-center whitespace-nowrap rounded-full text-xs font-medium ${
                      section.active
                        ? 'border border-green-200 bg-green-100 text-green-700'
                        : 'border border-gray-200 bg-gray-100 text-gray-600'
                    }`}>
                      {section.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {/* Deux champs a largeur fixe : ils se suivaient dans un
                      `gap-4`, donc leur position dependait de ce qui precedait
                      et rien ne s'alignait d'une carte a l'autre. */}
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                    <span className="inline-flex w-[92px] items-baseline gap-2">
                      <span className="text-xs text-gray-500">Ordre</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {section.ordre ?? '—'}
                      </span>
                    </span>
                    <span className="inline-flex w-[124px] items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 flex-shrink-0 rounded-full border border-black/10"
                        style={{ backgroundColor: section.couleur }}
                      />
                      <span className="text-sm text-gray-600">{section.couleur}</span>
                    </span>
                  </div>

                  {section.description && (
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                      {section.description}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="p-2.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Sauvegarder"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="p-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Annuler"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => onEdit(section.id)}
                    disabled={isLoading}
                    className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(section)}
                    disabled={isLoading}
                    className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleToggleClick}
                    disabled={isLoading}
                    className={`p-2.5 rounded-lg transition-colors disabled:opacity-50 ${
                      section.active 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={section.active ? 'Désactiver' : 'Activer'}
                  >
                    {section.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default ParametresClasseMatiere;